import React, { useState, useContext } from "react"
import {
  Typography,
  Button,
  ButtonGroup,
  Paper,
  Tooltip,
  TableCell,
  TableBody,
  TableContainer,
  TableRow,
  TableHead,
  Box,
  Table,
  IconButton,
  TextField,
  Input,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Modal,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import UserContext from "../../context/UserContext"
import { faecher } from "../../constants/faecher"
import useFetch from "../../hooks/useFetch"
import groupBooksByGrade from "../../functions/groupBooksByGrade"
import useMutation from "../../hooks/useMutation"

const CustomPaper = (props) => <Paper elevation={0} {...props} />

const emptyBook = {
  id: "",
  title: "",
  isbn: "",
  priceOriginal: "",
  subject: "",
  grade: "",
  comment: "",
  publisher: "",
}

const Bookmanagement = () => {
  const { userData } = useContext(UserContext)
  const schoolID = userData.school.id

  const [currentClass, setCurrentClass] = useState(5)
  const [createMode, setCreateMode] = useState(false)
  const [currentBook, setCurrentBook] = useState(emptyBook)
  const [newTitle, setNewTitle] = useState("")
  const [newISBN, setNewISBN] = useState("")
  const [newPrice, setNewPrice] = useState("")
  const [newComment, setNewComment] = useState("")
  const [newVerlag, setNewVerlag] = useState("")
  const [newFach, setNewFach] = useState("")
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [open, setOpen] = useState(false)
  const [snackbarMsg, setSnackbarMsg] = useState("")

  const { data: bookList, loading: booksLoading, refetch } = useFetch(`/school/${schoolID}/books`)
  const booksByGrade = !bookList || booksLoading ? [] : groupBooksByGrade(bookList.filter((b) => !b.isBundle))

  const { mutate: addBookMutation, loading: addBookLoading } = useMutation("/book")
  const { mutate: updateBookMutation, loading: updateBookLoading } = useMutation("/book", "PUT")
  const { mutate: deleteBookMutation, loading: deleteBookLoading } = useMutation("/book", "DELETE")

  const resetEntry = () => {
    setCreateMode(false)
    setCurrentBook(emptyBook)
    setNewTitle("")
    setNewISBN("")
    setNewPrice("")
    setNewComment("")
    setNewVerlag("")
    setNewFach("")
  }

  const handleClick = (book) => {
    setCurrentBook(book)
    const { title, isbn, priceOriginal, subject, comment, publisher } = book
    setNewTitle(title)
    setNewISBN(isbn)
    setNewPrice(priceOriginal)
    setNewComment(comment)
    setNewVerlag(publisher)
    setNewFach(subject)
  }

  const handleAddBook = async (event) => {
    event.preventDefault()
    try {
      await addBookMutation({
        title: newTitle,
        schoolID,
        isbn: newISBN.toString(),
        subject: newFach,
        grade: currentClass,
        priceOriginal: newPrice || 0,
        comment: newComment,
        publisher: newVerlag,
      })

      refetch()

      setOpen(true)
      setSnackbarMsg("Buch gespeichert")
    } catch (e) {
      console.log(e.message)
    }
    resetEntry()
  }

  const handleSaveEditedBook = async () => {
    try {
      await updateBookMutation({
        id: currentBook.id,
        title: newTitle,
        schoolID,
        isbn: newISBN.toString(),
        subject: newFach,
        grade: currentClass,
        priceOriginal: newPrice || 0,
        comment: newComment,
        publisher: newVerlag,
      })

      refetch()

      setOpen(true)
      setSnackbarMsg("Änderungen gespeichert")
    } catch (e) {
      console.log(e.message)
    }
    resetEntry()
  }

  const handleConfirmDeleteBook = async () => {
    try {
      await deleteBookMutation({
        id: currentBook.id,
        title: newTitle,
        schoolID,
        isbn: newISBN.toString(),
        subject: newFach,
        grade: currentClass,
        priceOriginal: newPrice || 0,
        comment: newComment,
        publisher: newVerlag,
      })

      refetch()

      setOpen(true)
      setSnackbarMsg("Buch gelöscht")
    } catch (e) {
      console.log(e.message)
    }
    resetEntry()
    setShowDeleteConfirm(false)
  }

  const rows = booksByGrade[currentClass] || []
  const loading = booksLoading || addBookLoading || updateBookLoading || deleteBookLoading

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: "1vw",
          left: "12vw",
          borderRadius: "38px",
          backgroundColor: "white",
          height: "calc(100vh - 2vw)",
          width: "87vw",
          p: "1vw",
          overflow: "auto",
          msOverflowStyle: "-ms-autohiding-scrollbar" /* IE and Edge */, // ????
          scrollbarWidth: "none" /* Firefox */,
        }}>
        <Typography variant="h5" align="left" sx={{ ml: "1vw", mt: "1vw", color: "bmsecondary.main" }}>
          {currentClass ? `Bücherliste Klasse ${currentClass}` : "Extrabücher"}
        </Typography>
        <Paper elevation={6} sx={{ width: "calc(100% - 2vw)", mx: "1vw", mt: "5vh", borderRadius: "20px" }}>
          <ButtonGroup
            variant="outlined"
            sx={{ width: "100%", height: "5vh", borderTopLeftRadius: "20px", borderTopRightRadius: "20px", overflow: "hidden" }}>
            {[5, 6, 7, 8, 9, 10, 11, 12, 13, 0].map((c) => (
              <Button
                onClick={() => setCurrentClass(c)}
                fullWidth
                key={c}
                sx={{
                  backgroundColor: c !== currentClass ? "bmprimary.main" : "white",
                  color: c === currentClass ? "bmsecondary.main" : "bmsecondary.main",
                  borderColor: "bmprimary.main",
                  "&:hover": { borderColor: "bmprimary.main" },
                  "&:last-child": { borderTopRightRadius: "20px" },
                  "&:first-child": { borderTopLeftRadius: "20px" },
                }}>
                {c ? c : "Extra"}
              </Button>
            ))}
          </ButtonGroup>

          <TableContainer
            sx={{ borderBottomRightRadius: "20px", borderBottomLeftRadius: "20px", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
            component={CustomPaper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "25%" }}>Titel</TableCell>
                  <TableCell sx={{ width: "10%" }} align="right">
                    Fach
                  </TableCell>
                  <TableCell sx={{ width: "15%" }} align="right">
                    ISBN
                  </TableCell>
                  <TableCell sx={{ width: "15%" }} align="right">
                    Verlag
                  </TableCell>
                  <TableCell sx={{ width: "10%" }} align="right">
                    Originalpreis
                  </TableCell>
                  <TableCell sx={{ width: "25%" }} align="right">
                    Information
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <>
                    {row?.id === currentBook?.id ? (
                      <>
                        <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                          <TableCell component="th" scope="row">
                            <TextField
                              autoFocus
                              required
                              id="title"
                              name="title"
                              placeholder="Titel *"
                              fullWidth
                              variant="standard"
                              onChange={(e) => setNewTitle(e.target.value)}
                              value={newTitle}
                            />
                          </TableCell>
                          <TableCell align="left">
                            <FormControl variant="standard" sx={{ minWidth: 120, width: "100%", m: 0 }}>
                              <Select
                                required
                                value={newFach}
                                displayEmpty
                                id="fach"
                                name="fach"
                                variant="standard"
                                renderValue={(selected) => {
                                  if (!selected || selected.length === 0) {
                                    return <Typography color="grey.400">Fach *</Typography>
                                  } else {
                                    // const temp = faecher.filter((f) => f.FID === newFach)
                                    // return temp[0]?.Name

                                    return newFach
                                  }
                                }}
                                onChange={(e) => setNewFach(e.target.value)}>
                                <MenuItem disabled value="">
                                  <em>Fach</em>
                                </MenuItem>
                                {faecher.map((f) => (
                                  <MenuItem key={f} value={f}>
                                    {f}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              fullWidth
                              required
                              id="isbn"
                              placeholder="ISBN *"
                              name="isbn"
                              variant="standard"
                              onChange={(event) => {
                                let rawVal = event.target.value
                                let a = rawVal.toString()
                                if (a === " " || a.length === 0 || (a.length === 1 && isNaN(a))) {
                                  setNewISBN("")
                                } else {
                                  setNewISBN(parseInt(a))
                                }
                              }}
                              value={newISBN}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              fullWidth
                              required
                              id="verlag"
                              placeholder="Verlag"
                              name="verlag"
                              variant="standard"
                              onChange={(event) => setNewVerlag(event.target.value)}
                              value={newVerlag}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Input
                              fullWidth
                              placeholder="Org. Preis"
                              id="price"
                              name="price"
                              endAdornment={<InputAdornment position="end">€</InputAdornment>}
                              variant="standard"
                              onChange={(event) => {
                                let rawVal = event.target.value
                                let a = rawVal.toString()
                                let c = a.replace(",", ".")
                                let b = c.replace(/\s/g, "")
                                if (b === " " || b.length === 0 || isNaN(b) || (b.match(".") || []).length > 1) {
                                  setNewPrice(0)
                                } else {
                                  setNewPrice(b)
                                }
                              }}
                              value={newPrice || ""}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              fullWidth
                              placeholder="Information"
                              required
                              id="comment"
                              name="comment"
                              variant="standard"
                              onChange={(event) => setNewComment(event.target.value)}
                              value={newComment}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                          <TableCell component="th" scope="row"></TableCell>
                          <TableCell component="th" scope="row"></TableCell>
                          <TableCell component="th" scope="row"></TableCell>
                          <TableCell component="th" scope="row"></TableCell>
                          <TableCell component="th" scope="row"></TableCell>
                          <TableCell component="th" scope="row" sx={{ float: "right", py: 1 }}>
                            <Button
                              variant="contained"
                              sx={{
                                fontSize: "smaller",
                                width: "30%",
                                mr: "3%",
                                backgroundColor: "bmprimary.main",
                                color: "white",
                                "&:hover": { backgroundColor: "bmsecondary.main" },
                              }}
                              onClick={handleSaveEditedBook}
                              disabled={!newTitle || !newISBN || !newFach || newTitle.length === 0 || newISBN.length === 0 || newFach.length === 0}>
                              Speichern
                            </Button>
                            <Button
                              variant="contained"
                              color="warning"
                              sx={{ fontSize: "smaller", width: "30%", mx: "1.5%" }}
                              onClick={() => setShowDeleteConfirm(true)}>
                              Löschen
                            </Button>
                            <Button
                              variant="contained"
                              sx={{
                                fontSize: "smaller",
                                width: "30%",
                                ml: "3%",
                                backgroundColor: "bmprimary.main",
                                color: "white",
                                "&:hover": { backgroundColor: "bmsecondary.main" },
                              }}
                              onClick={resetEntry}>
                              Abbrechen
                            </Button>
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow
                        onClick={() => handleClick(row)}
                        key={row.id}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                        <TableCell component="th" scope="row">
                          {row.title}
                        </TableCell>
                        <TableCell align="right">{row.subject}</TableCell>
                        <TableCell align="right">{row.isbn}</TableCell>
                        <TableCell align="right">{row.publisher}</TableCell>
                        <TableCell align="right">{row.priceOriginal ? `${row.priceOriginal} €` : ""}</TableCell>
                        <TableCell align="right">{row.comment}</TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
                {!createMode ? (
                  <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                    <TableCell component="th" scope="row" sx={{ py: 1 }}>
                      <Tooltip title="Neues Buch anlegen" placement="right" arrow>
                        <IconButton onClick={() => setCreateMode(true)}>
                          <AddCircleIcon fontSize="smaller" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                      <TableCell component="th" scope="row">
                        <TextField
                          autoFocus
                          required
                          id="title"
                          name="title"
                          placeholder="Titel *"
                          fullWidth
                          variant="standard"
                          onChange={(e) => setNewTitle(e.target.value)}
                          value={newTitle}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <FormControl variant="standard" sx={{ minWidth: 120, width: "100%", m: 0 }}>
                          <Select
                            required
                            value={newFach}
                            displayEmpty
                            id="fach"
                            name="fach"
                            variant="standard"
                            renderValue={(selected) => {
                              if (!selected || selected.length === 0) {
                                return <Typography color="grey.400">Fach *</Typography>
                              } else {
                                // const temp = faecher.filter((f) => f.FID === selected)
                                // return temp[0].Name

                                return selected
                              }
                            }}
                            onChange={(e) => setNewFach(e.target.value)}>
                            <MenuItem disabled value="">
                              <em>Fach</em>
                            </MenuItem>
                            {faecher.map((f) => (
                              <MenuItem key={f} value={f}>
                                {f}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          fullWidth
                          required
                          id="isbn"
                          placeholder="ISBN *"
                          name="isbn"
                          variant="standard"
                          onChange={(event) => {
                            let rawVal = event.target.value
                            let a = rawVal.toString()
                            if (a === " " || a.length === 0 || (a.length === 1 && isNaN(a))) {
                              setNewISBN("")
                            } else {
                              setNewISBN(parseInt(a))
                            }
                          }}
                          value={newISBN}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          fullWidth
                          required
                          id="verlag"
                          placeholder="Verlag"
                          name="verlag"
                          variant="standard"
                          onChange={(event) => setNewVerlag(event.target.value)}
                          value={newVerlag}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Input
                          fullWidth
                          placeholder="Org. Preis"
                          id="price"
                          name="price"
                          endAdornment={<InputAdornment position="end">€</InputAdornment>}
                          variant="standard"
                          onChange={(event) => {
                            let rawVal = event.target.value
                            let a = rawVal.toString()
                            let c = a.replace(",", ".")
                            let b = c.replace(/\s/g, "")
                            if (b === " " || b.length === 0 || isNaN(b) || (b.match(".") || []).length > 1) {
                              setNewPrice(0)
                            } else {
                              setNewPrice(b)
                            }
                          }}
                          value={newPrice || ""}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          fullWidth
                          placeholder="Information"
                          required
                          id="comment"
                          name="comment"
                          variant="standard"
                          onChange={(event) => setNewComment(event.target.value)}
                          value={newComment}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                      <TableCell component="th" scope="row"></TableCell>
                      <TableCell component="th" scope="row"></TableCell>
                      <TableCell component="th" scope="row"></TableCell>
                      <TableCell component="th" scope="row"></TableCell>
                      <TableCell component="th" scope="row"></TableCell>
                      <TableCell component="th" scope="row" sx={{ float: "right", py: 1 }}>
                        <Button
                          variant="contained"
                          sx={{
                            fontSize: "smaller",
                            width: "45%",
                            mr: "5%",
                            backgroundColor: "bmprimary.main",
                            color: "white",
                            "&:hover": { backgroundColor: "bmsecondary.main" },
                          }}
                          onClick={handleAddBook}
                          disabled={newTitle.length === 0 || newISBN.length === 0 || newFach.length === 0}>
                          Speichern
                        </Button>
                        <Button
                          variant="contained"
                          sx={{
                            fontSize: "smaller",
                            width: "45%",
                            ml: "5%",
                            backgroundColor: "bmprimary.main",
                            color: "white",
                            "&:hover": { backgroundColor: "bmsecondary.main" },
                          }}
                          onClick={resetEntry}>
                          Abbrechen
                        </Button>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              width: 400,
              borderRadius: "10px",
              boxShadow: 24,
              p: 4,
            }}>
            <Typography variant="h6" component="h2">
              Löschen bestätigen
            </Typography>
            <Typography sx={{ mt: 2, mb: 3 }}>
              <b>{newTitle}</b> löschen? <br />
              Dieser Vorgang kann nicht rückgängig gemacht werden.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              sx={{ width: "40%", color: "white", mx: "5%" }}
              onClick={handleConfirmDeleteBook}
              startIcon={<DeleteIcon />}>
              Löschen
            </Button>
            <Button variant="contained" sx={{ width: "40%", color: "white", mx: "5%" }} onClick={() => setShowDeleteConfirm(false)}>
              Abbrechen
            </Button>
          </Box>
        </Modal>
      </Box>

      <Snackbar
        sx={{ color: "bmsecondary.main" }}
        open={open}
        autoHideDuration={10000}
        onClose={(event, reason) => {
          if (reason === "clickaway") return
          setOpen(false)
        }}>
        <Alert onClose={() => setOpen(false)} severity="success" sx={{ width: "100%" }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <Backdrop sx={(theme) => ({ backgroundColor: "rgba(0,0,0,0.1)", zIndex: theme.zIndex.drawer + 1 })} open={loading}>
        <CircularProgress size={60} thickness={5} color="secondary" />
      </Backdrop>
    </>
  )
}

export default Bookmanagement
